// src/utils/ledgerHelpers.js

/**
 * Tipos de entrada do ledger resolvidos para exibição no frontend.
 * @typedef {'credit'|'debit_withdrawal'|'debit_transfer_fee'|'refund'|'payout'|'payout_pending'|'payout_failed'|'blocked'|'available'} ResolvedEntryType
 */

/**
 * Resolve o tipo de exibição de uma entrada do ledger.
 * Leva em conta o entry_type bruto, withdrawal_id e withdrawal_status.
 * @param {Object} entry
 * @returns {ResolvedEntryType}
 */
export function getEntryType(entry) {
  if (!entry) return 'available';
  const rawType = entry.entry_type || entry.entryType || entry.type || '';

  if (rawType === 'debit_withdrawal' || rawType === 'withdrawal') return 'debit_withdrawal';
  if (rawType === 'debit_transfer_fee') return 'debit_transfer_fee';
  if (rawType === 'refund') return 'refund';

  if (entry.withdrawal_id) {
    const wStatus = entry.withdrawal_status;
    if (wStatus === 'completed') return 'payout';
    if (wStatus === 'failed' || wStatus === 'cancelled') return 'payout_failed';
    return 'payout_pending';
  }

  if (!entry.is_withdrawable) return 'blocked';
  return 'available';
}

/** Mapa de ícones FontAwesome por tipo de entrada. */
const ENTRY_ICONS = {
  debit_withdrawal:    'fa-circle-check',
  debit_transfer_fee:  'fa-receipt',
  refund:              'fa-rotate-left',
  payout:              'fa-arrow-up-from-bracket',
  payout_pending:      'fa-arrow-up-from-bracket',
  payout_failed:       'fa-triangle-exclamation',
  blocked:             'fa-clock',
  available:           'fa-circle-check',
};

/**
 * Retorna o ícone FontAwesome correspondente ao tipo de entrada.
 * @param {Object} entry
 * @returns {string}
 */
export function entryIcon(entry) {
  return ENTRY_ICONS[getEntryType(entry)] || 'fa-circle-check';
}

/** Mapa de rótulos por tipo de entrada. */
const ENTRY_LABELS = {
  debit_withdrawal:    'Transferência realizada',
  debit_transfer_fee:  'Taxa de transferência',
  refund:              'Estorno',
  payout:              'Transferência realizada',
  payout_pending:      'Transferência em andamento',
  payout_failed:       'Transferência não concluída',
  blocked:             'Pagamento recebido',
  available:           'Recebimento',
};

/**
 * Retorna o rótulo descritivo da entrada do ledger.
 * @param {Object} entry
 * @returns {string}
 */
export function entryLabel(entry) {
  return ENTRY_LABELS[getEntryType(entry)] || 'Recebimento';
}

/** Mapa de rótulos de status por tipo de entrada. */
const ENTRY_STATUS_LABELS = {
  debit_withdrawal:    'Enviado com sucesso',
  debit_transfer_fee:  'Descontado',
  refund:              'Estornado',
  payout:              'Transferido',
  payout_pending:      'Em transferência',
  payout_failed:       'Falhou',
  blocked:             'Aguardando liberação',
  available:           'Disponível',
};

/**
 * Retorna o rótulo de status da entrada do ledger.
 * @param {Object} entry
 * @returns {string}
 */
export function entryStatusLabel(entry) {
  return ENTRY_STATUS_LABELS[getEntryType(entry)] || 'Disponível';
}

/**
 * Mapa simples de TYPE_LABELS para uso em contextos admin.
 * entry_type bruto → rótulo.
 */
export const TYPE_LABELS = {
  credit:              'Recebimento',
  refund:              'Estorno',
  debit_withdrawal:    'Transferência realizada',
  debit_transfer_fee:  'Taxa de transferência',
};

/**
 * Retorna o rótulo de um entry_type bruto (sem resolução de withdrawal_status).
 * Útil para contextos admin onde o tipo é exibido diretamente.
 * @param {string} type
 * @returns {string}
 */
export function typeLabel(type) {
  return TYPE_LABELS[type] ?? type;
}

/**
 * Retorna a classe CSS de cor para o valor de uma entrada do ledger.
 * @param {Object} entry
 * @returns {string}
 */
export function getAmountClass(entry) {
  const type = getEntryType(entry);
  if (type === 'debit_withdrawal' || type === 'debit_transfer_fee') return 'text-secondary';
  if (type === 'refund') return 'text-danger';
  if (type === 'payout_pending') return 'text-warning';
  if (type === 'payout_failed') return 'text-danger';
  return (entry?.net_amount ?? 0) < 0 ? 'text-danger' : 'text-success';
}

/**
 * Indica se uma entrada do ledger pode ter seus detalhes expandidos.
 * @param {Object} entry
 * @returns {boolean}
 */
export function isExpandable(entry) {
  const type = getEntryType(entry);
  return type !== 'refund'
      && type !== 'payout'
      && type !== 'payout_pending'
      && type !== 'payout_failed'
      && type !== 'debit_withdrawal'
      && type !== 'debit_transfer_fee';
}
